import {Seconds} from '@store/@types/util';
import {Call} from '@store/call-card/Call';
import throttle from 'lodash/throttle';
import {SetState} from '@store/@types/util/React';
import {registerEvents} from 'utils/registerEvents';
import {TopicMatch, TranscriptBlock} from '@store/call-card/CallCard.types';
import {List, Map} from 'immutable';
import {
  getNextValidTime,
  isValidTime,
  SharedAreas,
} from '@components/call-card/SharedHighlights.utils';

export type PlayerRef = HTMLAudioElement | HTMLVideoElement | null;
// ID used for the bottom edge of the speaker list component. This element determines
// how long our player tick should be.
export const PlayerTickGuideId = 'player-tick-guide';

/**
 * Playback information/controls used by several components
 */
export interface PlayerProps {
  duration: Seconds;
  currentTime: Seconds;
  paused: boolean;
}

export function getPlayerProps(player: PlayerRef, call: Call): PlayerProps {
  if (!player) {
    return {
      duration: call.duration,
      currentTime: 0,
      paused: true,
    };
  }

  const duration = isNaN(player.duration) ? call.duration : player.duration;
  return {
    duration,
    currentTime: player.currentTime,
    paused: player.paused,
  };
}

/**
 * Events to handle player props which are fed to all call card children
 */
export function registerPlayerEvents(
  playerRef: PlayerRef,
  call: Call,
  setPlayerProps: SetState<ReturnType<typeof getPlayerProps>>
): void | (() => void) {
  if (!playerRef) return;
  const updatePlayerProps = () => setPlayerProps(getPlayerProps(playerRef, call));
  const throttleUpdate = throttle(updatePlayerProps, 250);

  return registerEvents(playerRef, [
    ['timeupdate', throttleUpdate],
    ['pause', updatePlayerProps],
    ['play', updatePlayerProps],
    ['canplay', updatePlayerProps],
    ['seeking', updatePlayerProps],
  ]);
}

export function scrollTranscriptToTime(time: Seconds) {
  const focusedEl = document.querySelector(`.transcript-line-${Math.floor(time)}`);
  if (!focusedEl) return;
  // @ts-ignore offsetTop does indeed exist
  focusedEl.parentElement.scroll({top: focusedEl.offsetTop - 10, behavior: 'smooth'});
}

function scrollTranscriptLine(event) {
  const time = event.target.currentTime;
  scrollTranscriptToTime(time);
}

export function registerTranscriptScrollEvents(
  playerRef: PlayerRef,
  transcript: List<TranscriptBlock>,
  matches: Map<Seconds, List<TopicMatch>>
) {
  if (!playerRef) return;
  const scrollAfterSeek = event => {
    const currentTime = event.target.currentTime;

    let scrollElement;
    if (matches.isEmpty()) {
      const focusedBlock = transcript.findLast(b => b.startTime < currentTime);
      if (focusedBlock) {
        scrollElement = document.querySelector(
          `.transcript-line-${Math.floor(focusedBlock.startTime)}`
        );
      }
    } else {
      const matchedTime = matches.findLastKey((_, time) => time < currentTime);
      if (matchedTime !== undefined) {
        scrollElement = document.querySelector(`.transcript-line-${Math.floor(matchedTime)}`);
      }
    }

    // @ts-ignore these fields do exist.
    scrollElement?.parentElement.scroll({top: scrollElement.offsetTop - 10, behavior: 'smooth'});
  };

  return registerEvents(playerRef, [
    ['timeupdate', scrollTranscriptLine],
    ['seeked', scrollAfterSeek],
  ]);
}

export function registerSharedAreaEvents(playerRef: PlayerRef, sharedAreas: SharedAreas) {
  if (!playerRef || sharedAreas.isEmpty()) return;
  const effectiveEnd = sharedAreas.map(([_start, end]) => end).max();

  const validateTime = () => {
    if (Number.isNaN(playerRef.currentTime)) return;
    if (isValidTime(sharedAreas, playerRef.currentTime)) return;
    const nextTime = getNextValidTime(sharedAreas, playerRef.currentTime);
    playerRef.currentTime = nextTime;
    if (nextTime >= effectiveEnd) {
      playerRef.pause();
    }
  };

  return registerEvents(playerRef, [['timeupdate', validateTime]]);
}
